import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "../../shared/context/SidebarContext";
import { toast } from "react-toastify";
import { format } from "date-fns";
import api from "../../shared/services/api";
import {
    loadCampanhasService,
    createCampanhaService,
    updateCampanhaService,
    toggleCampanhaService,
    deleteCampanhaService,
    loadSessoesService,
} from "./services/campanhasService";
import {
    Plus, Edit2, Power, Trash2, MessageSquare, Users, ChevronRight, Upload, X, UserPlus, Eye, Hash
} from "lucide-react";

export default function WhatsAppCampanhas() {
    const navigate = useNavigate();
    const { isOpen } = useContext(SidebarContext);
    const [campanhas, setCampanhas] = useState([]);
    const [sessoes, setSessoes] = useState([]);
    const [dominios, setDominios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Form state
    const [form, setForm] = useState({
        titulo: "", descricao: "", tipo: "grupo", imagem_url: "", automatizar: false, session_name: "",
        admins: "", espioes: "", limite_membros: 200, dominio_id: "",
        grupo_somente_admins: false, grupo_edicao_admins: false
    });

    const carregar = async () => {
        setLoading(true);
        try {
            const [c, s, d] = await Promise.all([
                loadCampanhasService(), 
                loadSessoesService(),
                api.get('/dominios').then(res => res.data).catch(() => [])
            ]);
            setCampanhas(c);
            setSessoes(s.filter(ss => ss.status === "conectado" || ss.status === "open"));
            setDominios(d.filter(dom => dom.status === 'pendente' || dom.status === 'ativo'));
        } catch {
            toast.error("Erro ao carregar campanhas");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const abrirModal = (campanha = null) => {
        if (campanha) {
            setEditando(campanha);
            setForm({
                titulo: campanha.titulo,
                descricao: campanha.descricao || "",
                tipo: campanha.tipo,
                imagem_url: campanha.imagem_url || "",
                automatizar: campanha.automatizar || false,
                session_name: campanha.session_name || "",
                admins: Array.isArray(campanha.admins) ? campanha.admins.join(", ") : (campanha.admins || ""),
                espioes: Array.isArray(campanha.espioes) ? campanha.espioes.join(", ") : (campanha.espioes || ""),
                limite_membros: campanha.limite_membros || 200,
                dominio_id: campanha.dominio_id || "",
                grupo_somente_admins: campanha.grupo_somente_admins || false,
                grupo_edicao_admins: campanha.grupo_edicao_admins || false
            });
        } else {
            setEditando(null);
            setForm({
                titulo: "", descricao: "", tipo: "grupo", imagem_url: "", automatizar: false,
                session_name: sessoes[0]?.session_name || "", admins: "", espioes: "", limite_membros: 200, dominio_id: "",
                grupo_somente_admins: false, grupo_edicao_admins: false
            });
        }
        setModalAberto(true);
    };

    // Upload de imagem
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setUploading(true);
        try {
            const res = await api.post("/upload", formData);
            setForm((prev) => ({ ...prev, imagem_url: res.data.url }));
            toast.success("Imagem enviada!");
        } catch {
            toast.error("Erro ao enviar imagem");
        } finally {
            setUploading(false);
        }
    };

    const salvar = async () => {
        if (!form.titulo.trim()) { toast.warn("Preencha o título"); return; }
        try {
            const payload = {
                ...form,
                admins: form.admins ? form.admins.split(",").map(a => a.trim()).filter(Boolean) : null,
                espioes: form.espioes ? form.espioes.split(",").map(e => e.trim()).filter(Boolean) : null,
                limite_membros: parseInt(form.limite_membros) || 200,
                dominio_id: form.dominio_id ? parseInt(form.dominio_id) : null,
            };
            if (editando) {
                await updateCampanhaService(editando.id, payload);
                toast.success("Campanha atualizada!");
            } else {
                await createCampanhaService(payload);
                toast.success("Campanha criada!");
            }
            setModalAberto(false);
            carregar();
        } catch {
            toast.error("Erro ao salvar campanha");
        }
    };

    const alternar = async (c) => {
        try {
            await toggleCampanhaService(c.id, !c.ativa);
            toast.success(c.ativa ? "Campanha inativada" : "Campanha ativada");
            carregar();
        } catch {
            toast.error("Erro ao alterar status");
        }
    };

    const excluir = async (c) => {
        if (!confirm(`Excluir campanha "${c.titulo}"? Essa ação é irreversível.`)) return;
        try {
            await deleteCampanhaService(c.id);
            toast.success("Campanha excluída");
            carregar();
        } catch {
            toast.error("Erro ao excluir");
        }
    };

    if (loading) {
        return (
            <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} p-6 min-h-screen bg-base text-white flex items-center justify-center`}>
                <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
            </main>
        );
    }

    return (
        <main className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto min-h-screen text-ink-1 mb-16 sm:mb-0`}>

            <header className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 animate-fade-in">
                <div className="space-y-1">
                    <span className="label-overline">WhatsApp</span>
                    <h1 className="display-2 flex items-center gap-3">
                        Campanhas
                        <MessageSquare className="h-7 w-7 text-brand-400" strokeWidth={2.2} />
                    </h1>
                    <p className="text-ink-3 text-sm">
                        Crie campanhas de grupos rotativos com link único e gestão automática de admins.
                    </p>
                </div>
                <button onClick={() => abrirModal()} className="btn-primary !py-2.5">
                    <Plus className="h-4 w-4" /> Nova Campanha
                </button>
            </header>

            {/* Grid de Campanhas */}
            {campanhas.length === 0 ? (
                <div className="text-center py-20 text-ink-4">
                    <p className="text-lg">Nenhuma campanha cadastrada</p>
                    <p className="text-sm mt-1">Crie sua primeira campanha para começar!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {campanhas.map((c) => (
                        <div
                            key={c.id}
                            className={`rounded-xl border p-4 transition-all hover:border-emerald-600/50 cursor-pointer ${c.ativa ? "bg-surface-1 border-white/[0.06]" : "bg-surface-1/50 border-white/[0.06] opacity-60"
                                }`}
                            onClick={() => navigate(`/whatsapp/campanhas/${c.id}`)}
                        >
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface-2 flex-shrink-0 flex items-center justify-center">
                                    {c.imagem_url ? (
                                        <img src={c.imagem_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-xl">📢</span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold truncate">{c.titulo}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${c.tipo === "grupo" ? "bg-blue-900/50 text-blue-400" : "bg-brand-900/50 text-brand-400"
                                            }`}>
                                            {c.tipo}
                                        </span>
                                        {c.limite_membros && (
                                            <span className="text-xs text-ink-4">max: {c.limite_membros}</span>
                                        )}
                                    </div>
                                </div>
                                <ChevronRight size={16} className="text-ink-5 flex-shrink-0 mt-1" />
                            </div>

                            <div className="flex gap-4 text-xs text-ink-3 mb-3">
                                <div className="flex items-center gap-1">
                                    <Users size={12} />
                                    <span>{c.total_grupos || 0} grupos</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <MessageSquare size={12} />
                                    <span>{c.total_participantes || 0} membros</span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-xs text-ink-4">
                                    {c.data_criacao ? format(new Date(c.data_criacao), "dd/MM/yyyy") : "-"}
                                </span>
                                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => abrirModal(c)} className="p-1.5 rounded hover:bg-surface-2 text-ink-3 hover:text-blue-400 transition" title="Editar">
                                        <Edit2 size={14} />
                                    </button>
                                    <button onClick={() => alternar(c)} className={`p-1.5 rounded hover:bg-surface-2 transition ${c.ativa ? "text-ink-3 hover:text-yellow-400" : "text-ink-3 hover:text-emerald-400"}`} title={c.ativa ? "Inativar" : "Ativar"}>
                                        <Power size={14} />
                                    </button>
                                    <button onClick={() => excluir(c)} className="p-1.5 rounded hover:bg-surface-2 text-ink-3 hover:text-red-400 transition" title="Excluir">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Nova/Editar Campanha */}
            {modalAberto && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="bg-surface-1 rounded-xl border border-white/[0.08] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h2 className="text-lg font-bold mb-4">{editando ? "Editar Campanha" : "Nova Campanha"}</h2>

                        <div className="space-y-4">
                            {/* Título */}
                            <div>
                                <label className="text-sm text-ink-3 block mb-1">Título *</label>
                                <input
                                    value={form.titulo}
                                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                    placeholder="Nome da campanha"
                                />
                            </div>

                            {/* Descrição */}
                            <div>
                                <label className="text-sm text-ink-3 block mb-1">Descrição</label>
                                <textarea
                                    value={form.descricao}
                                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 h-20 resize-none"
                                    placeholder="Descrição da campanha"
                                />
                            </div>

                            {/* Tipo + Sessão */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm text-ink-3 block mb-1">Tipo</label>
                                    <select
                                        value={form.tipo}
                                        onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                                        className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm"
                                    >
                                        <option value="grupo">Grupo</option>
                                        <option value="comunidade">Comunidade</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm text-ink-3 block mb-1">Sessão</label>
                                    <select
                                        value={form.session_name}
                                        onChange={(e) => setForm({ ...form, session_name: e.target.value })}
                                        className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm"
                                    >
                                        <option value="">Selecionar...</option>
                                        {sessoes.map((s) => (
                                            <option key={s.id} value={s.session_name}>
                                                {s.nome} · {s.provider || "whatsmeow"}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Domínio Customizado */}
                            <div>
                                <label className="text-sm text-ink-3 block mb-1">Domínio Customizado (Link de Rotação)</label>
                                <select
                                    value={form.dominio_id}
                                    onChange={(e) => setForm({ ...form, dominio_id: e.target.value })}
                                    className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm"
                                >
                                    <option value="">Padrão da Plataforma</option>
                                    {dominios.map((d) => (
                                        <option key={d.id} value={d.id}>{d.url}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Imagem com Upload */}
                            <div>
                                <label className="text-sm text-ink-3 block mb-1">Imagem da Campanha</label>
                                <div className="flex items-center gap-3">
                                    {/* Preview */}
                                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-2 flex-shrink-0 flex items-center justify-center border border-white/[0.08]">
                                        {form.imagem_url ? (
                                            <img src={form.imagem_url} alt="Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <Upload size={20} className="text-ink-4" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="hidden"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="flex items-center gap-2 bg-surface-2 hover:bg-surface-3 border border-white/[0.08] px-3 py-2 rounded-lg text-sm transition disabled:opacity-50"
                                        >
                                            {uploading ? (
                                                <>
                                                    <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
                                                    Enviando...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload size={14} />
                                                    Procurar imagem
                                                </>
                                            )}
                                        </button>
                                        {form.imagem_url && (
                                            <button
                                                onClick={() => setForm({ ...form, imagem_url: "" })}
                                                className="mt-1 text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                                            >
                                                <X size={12} /> Remover
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Separador visual */}
                            <div className="border-t border-white/[0.08] pt-4">
                                <h3 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-2">
                                    <UserPlus size={14} /> Contatos do Grupo
                                </h3>

                                {/* Administradores */}
                                <div className="mb-3">
                                    <label className="text-sm text-ink-3 block mb-1">
                                        Administradores
                                        <span className="text-xs text-ink-4 ml-1">(números separados por vírgula)</span>
                                    </label>
                                    <input
                                        value={form.admins}
                                        onChange={(e) => setForm({ ...form, admins: e.target.value })}
                                        className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                        placeholder="5511999999999, 5511888888888"
                                    />
                                    <p className="text-xs text-ink-4 mt-1">Serão adicionados como admins nos grupos criados</p>
                                </div>

                                {/* Espiões */}
                                <div className="mb-3">
                                    <label className="text-sm text-ink-3 block mb-1 flex items-center gap-1">
                                        <Eye size={12} /> Espiões
                                        <span className="text-xs text-ink-4 ml-1">(participantes sem ser admin)</span>
                                    </label>
                                    <input
                                        value={form.espioes}
                                        onChange={(e) => setForm({ ...form, espioes: e.target.value })}
                                        className="w-full bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                        placeholder="5511777777777, 5511666666666"
                                    />
                                    <p className="text-xs text-ink-4 mt-1">Serão adicionados ao grupo sem permissão de admin</p>
                                </div>
                            </div>

                            {/* Limite de membros */}
                            <div className="border-t border-white/[0.08] pt-4">
                                <h3 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-2">
                                    <Hash size={14} /> Configurações de Grupos
                                </h3>
                                <div>
                                    <label className="text-sm text-ink-3 block mb-1">Limite de membros por grupo</label>
                                    <input
                                        type="number"
                                        min="2"
                                        max="1024"
                                        value={form.limite_membros}
                                        onChange={(e) => setForm({ ...form, limite_membros: e.target.value })}
                                        className="w-32 bg-surface-2 border border-white/[0.08] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                                    />
                                    <p className="text-xs text-ink-4 mt-1">Ao atingir o limite, um novo grupo será criado automaticamente</p>
                                </div>
                                <div className="mt-3 space-y-2">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={form.grupo_somente_admins}
                                            onChange={(e) => setForm({ ...form, grupo_somente_admins: e.target.checked })}
                                            className="rounded"
                                        />
                                        <span className="text-ink-3">Somente admins enviam mensagens</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={form.grupo_edicao_admins}
                                            onChange={(e) => setForm({ ...form, grupo_edicao_admins: e.target.checked })}
                                            className="rounded"
                                        />
                                        <span className="text-ink-3">Somente admins editam dados do grupo</span>
                                    </label>
                                    <p className="text-xs text-ink-4">Aplicado aos grupos criados automaticamente. Nome, foto e descrição são herdados do primeiro grupo da campanha.</p>
                                </div>
                            </div>

                            {/* Automatizar */}
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={form.automatizar}
                                    onChange={(e) => setForm({ ...form, automatizar: e.target.checked })}
                                    className="rounded"
                                />
                                <span className="text-ink-3">Automatizar criação de grupo</span>
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setModalAberto(false)} className="px-4 py-2 rounded-lg bg-surface-2 hover:bg-surface-3 text-sm transition">
                                Cancelar
                            </button>
                            <button onClick={salvar} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-sm font-medium transition">
                                {editando ? "Salvar" : "Criar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
